import { useEffect, useRef, useState } from "react"
import type { PresenceStatus } from "@/features/presence/store/presence.store"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  Bell, Download, Shield, Palette, Lock, ChevronRight,
  User, CircleDot, Moon, Database, ChevronDown, QrCode, Star,
  Edit2, Camera, Check, X,
} from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Input } from "@/clientWeb/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/clientWeb/components/ui/select"
import { DatePicker } from "@/clientWeb/components/ui/date-picker"
import { TimePicker } from "@/clientWeb/components/ui/time-picker"
import { earliestTimeFor, isBefore } from "@/features/meetings/utils/schedule-time"
import { useSelfPresence } from "@/features/presence/hooks/useSelfPresence"
import { Button } from "@/clientWeb/components/ui/button"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useAttachmentSettings } from "./store/attachment-settings.store"
import { useChatPrivacy } from "./hooks/useChatPrivacy"
import { useChatPreferences } from "./hooks/useChatPreferences"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { userDisplayName, userHandle } from "@/features/auth/utils/display-name"
import { AvatarPhoto } from "./avatar-photo"
import {
  CLEAR_AFTER_OPTIONS, CLEAR_AFTER_TIME_STEP, type ClearAfter, type ClearAfterState,
  clearAfterFor, defaultCustomClearAfter, formatClearsAt, recallClearAfter, rememberClearAfter,
  resolveClearAfter, sameClearAfter,
} from "./clear-after"

// `null` stands for Available: it clears the manual override rather than
// setting one, so the server's real (auto) status shows through. There is no
// "Out of Office" entry: `@manual_statuses` on the server only accepts
// online/away/busy/dnd, so that option could never be saved.
type ManualStatus = PresenceStatus | null

const STATUS_OPTIONS: { status: ManualStatus; labelKey: string; color: string; dot: string }[] = [
  { status: null, labelKey: "presence.available", color: "text-green-600 dark:text-green-400", dot: "bg-green-500" },
  { status: "busy", labelKey: "presence.statusBusy", color: "text-amber-600 dark:text-amber-400", dot: "bg-amber-500" },
  { status: "away", labelKey: "presence.statusAway", color: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500" },
  { status: "dnd", labelKey: "presence.statusDoNotDisturb", color: "text-destructive", dot: "bg-destructive" },
]

/** The option matching the server's manual status; unset or unrecognized reads as Available. */
function statusOptionFor(manualStatus: PresenceStatus | null | undefined) {
  return STATUS_OPTIONS.find(o => o.status === (manualStatus ?? null)) ?? STATUS_OPTIONS[0]
}

// "Clear After" (Today / 1 Hour / …) was once pure local state — never sent,
// never stored — and was removed for promising an expiry nothing kept. It is
// back now that presence carries one: the dialog sends `customMessageExpiresAt`
// with the message, signaling stores it beside the message, drops a lapsed
// message on read and clears it from each live socket's meta on a timer. The
// options and their times are in `./clear-after`.

interface ToggleProps { checked: boolean; onChange: (v: boolean) => void }
function Toggle({ checked, onChange }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      /* Both halves of this control have to contrast with EACH OTHER, not just
         with the page.
         The off track was `bg-muted-foreground/30`: thirty percent alpha, so
         the surface behind showed through. On a light theme that left a
         near-white track under a `bg-card` knob, which is white — the switch
         disappeared, and off could not be told from on.
         `bg-muted-foreground` is opaque and mid-tone in both themes
         (oklch 0.556 light, #a3a3a3 dark), so the white-ish knob reads against
         it in light and the dark knob reads against it in dark. */
      className={`relative w-10 h-5.5 rounded-full transition-colors shrink-0
        ring-1 ring-inset ring-black/10 dark:ring-white/10
        ${checked ? "bg-primary" : "bg-muted-foreground"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-card shadow-sm ring-1 ring-black/5 transition-transform
          ${checked ? "translate-x-[18px]" : "translate-x-0"}`}
      />
    </button>
  )
}

function SettingRow({ icon: Icon, label, sublabel, right, onClick }: { icon: React.ElementType; label: string; sublabel?: string; right?: React.ReactNode; onClick?: () => void }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer" onClick={onClick}>
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {sublabel && <p className="text-xs text-muted-foreground mt-0.5">{sublabel}</p>}
      </div>
      {right ?? <ChevronRight className="w-4 h-4 text-muted-foreground/50" />}
    </div>
  )
}

/** Up to two initials from a display name, for the no-avatar fallback. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? "")
    .join("") || "?"
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-4 pt-5 pb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
      {children}
    </p>
  )
}

interface SettingsTabProps {
  onOpenBlockedContacts?: () => void
  onOpenChatBackground?: () => void
  onOpenChatColor?: () => void
  onOpenStarred?: () => void
  profileName?: string
  profileImage?: string
  onUpdateProfile?: (name: string, image: string) => void
}

export function SettingsTab({ onOpenBlockedContacts, onOpenChatBackground, onOpenChatColor, onOpenStarred, profileName, profileImage, onUpdateProfile }: SettingsTabProps) {
  const t = useTranslation()
  // `profileName` is the caller's override; fall back to the signed-in user
  // rather than a hardcoded name so this never shows a stranger's identity.
  const authUser = useAuthStore((s) => s.user)
  const displayName = profileName || userDisplayName(authUser)
  const fileRef = useRef<HTMLInputElement>(null)
  const [statusPickerOpen, setStatusPickerOpen] = useState(false)
  const statusPickerRef = useRef<HTMLDivElement>(null)

  // The picker had no other way to close besides picking an option: not on
  // an outside click, not on Escape, and not when the status message dialog
  // opened on top of it.
  useEffect(() => {
    if (!statusPickerOpen) return
    const handlePointerDown = (e: MouseEvent) => {
      if (statusPickerRef.current && !statusPickerRef.current.contains(e.target as Node)) {
        setStatusPickerOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStatusPickerOpen(false)
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [statusPickerOpen])
  // Server-backed, so they hold across reloads and follow the user to their
  // other devices. Previously local `useState(false)` — a toast and nothing
  // else. Enforcement is server-side at the push fan-out: muting stops the
  // NOTIFICATION only; messages still arrive and unread counts still climb.
  const { preferences: notifPrefs, setMuteGroups, setMuteDirect } = useChatPreferences()
  // Auto-download is a per-device bandwidth policy, so it lives in a
  // localStorage-backed store rather than component state (which reset on every
  // reload) or the SDK (which would sync it across devices).
  const autoPhotos = useAttachmentSettings((s) => s.photos)
  const autoDocs = useAttachmentSettings((s) => s.documents)
  const autoVideos = useAttachmentSettings((s) => s.videos)
  const autoAudio = useAttachmentSettings((s) => s.audio)
  const setAutoDownload = useAttachmentSettings((s) => s.set)
  // Server-backed, so they hold across reloads and follow the user to their
  // other devices. Previously plain `useState(true)`: the toggle showed a toast,
  // changed nothing, and reset on reload.
  const { privacy, setReadReceipts, setShowStatus } = useChatPrivacy()
  const [backupExpanded, setBackupExpanded] = useState(false)
  const [backupIncludeMedia, setBackupIncludeMedia] = useState(true)
  const [backupAutoBackup, setBackupAutoBackup] = useState(false)
  const [backupFrequency, setBackupFrequency] = useState("Weekly")
  /**
   * The status message, read from and written to presence.
   *
   * This was `useState("")` — local to this component and sent nowhere. Saving
   * updated that variable and showed a success toast, so it looked right until
   * anything unmounted the tab, at which point the message was gone and the row
   * offered to add one again. Nothing was ever stored, so there was never
   * anything to edit.
   *
   * `useSelfPresence` already owns this: it reads my row from the presence
   * store and writes through `presenceApi.setMe`, optimistically, rolling back
   * if the request fails. The socket then re-confirms the write, so every
   * surface showing my status agrees without this tab telling any of them.
   */
  const { me: selfPresence, setStatus, clearStatus, setCustomMessage } = useSelfPresence()

  const statusMessage = selfPresence?.customMessage ?? ""
  const statusMessageExpiresAt = selfPresence?.customMessageExpiresAt ?? null

  /** Available clears the manual override; every other option sets it. On
   * failure `useSelfPresence` has already rolled the optimistic patch back, so
   * the picker is showing the previous value again by the time this toast
   * appears. */
  const handleSelectStatus = async (status: ManualStatus) => {
    setStatusPickerOpen(false)
    try {
      if (status === null) await clearStatus()
      else await setStatus(status)
    } catch {
      toast.error({ title: t("presence.statusUpdateFailed") })
    }
  }
  const [statusMsgDialogOpen, setStatusMsgDialogOpen] = useState(false)
  const [draftMessage, setDraftMessage] = useState("")
  const [draftClearAfter, setDraftClearAfter] = useState<ClearAfterState>({ choice: "today" })
  /** The dialog as it was when it opened — Save enables on any change to it,
   *  so changing only Clear After on an unchanged message can still be saved. */
  const [dialogBaseline, setDialogBaseline] = useState<{ message: string; clearAfter: ClearAfterState }>({
    message: "",
    clearAfter: { choice: "today" },
  })

  /** Open the editor seeded with whatever is currently set, expiry included —
   *  on the option that set it ("1 Hour") when this device saved it. */
  const openStatusMessageDialog = () => {
    setStatusPickerOpen(false)
    const clearAfter = clearAfterFor(statusMessage, statusMessageExpiresAt, recallClearAfter())
    setDraftMessage(statusMessage)
    setDraftClearAfter(clearAfter)
    setDialogBaseline({ message: statusMessage, clearAfter })
    setStatusMsgDialogOpen(true)
  }

  const handleClearAfterChoice = (choice: ClearAfter) => {
    setDraftClearAfter(prev =>
      choice !== "custom"
        ? { choice }
        // Keep a custom date/time already filled in; otherwise start valid.
        : { choice, ...(prev.date && prev.time ? { date: prev.date, time: prev.time } : defaultCustomClearAfter(new Date())) },
    )
  }

  // Read at render rather than held in state, so each interaction judges the
  // custom time against the clock as it is then; Save re-checks on click.
  const draftTrimmed = draftMessage.trim()
  const draftExpiry = resolveClearAfter(draftClearAfter, new Date())
  const customExpiryInvalid = draftClearAfter.choice === "custom" && draftExpiry === undefined
  const dialogDirty =
    draftTrimmed !== dialogBaseline.message.trim() ||
    // Clear After means nothing without a message to clear.
    (draftTrimmed !== "" && !sameClearAfter(draftClearAfter, dialogBaseline.clearAfter))
  // A floor for the time picker on TODAY only; every later day is open from midnight.
  const customTimeFloor =
    earliestTimeFor(draftClearAfter.date, new Date(Date.now() + 60_000), CLEAR_AFTER_TIME_STEP) ?? undefined

  /** Remove the status message outright, without going through the editor. */
  const handleClearStatusMessage = async () => {
    // Confirm after it lands, not before. Fired synchronously alongside the
    // request, "Status message cleared" appeared whatever happened — including
    // on the failure path, which then raised its own error toast on top, so the
    // one moment the user needed a straight answer gave them two contradictory
    // ones.
    try {
      await setCustomMessage(null)
      toast.success({
        title: "Status message cleared",
        description: "Your status message has been removed.",
      })
    } catch {
      toast.error({ title: "Couldn't clear status message" })
    }
  }
  const [showQr, setShowQr] = useState(false)
  const [isBackingUp, setIsBackingUp] = useState(false)
  const [backupProgress, setBackupProgress] = useState(0)
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [editName, setEditName] = useState(displayName)
  const [editImage, setEditImage] = useState("")

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      if (typeof ev.target?.result === "string") setEditImage(ev.target.result)
    }
    reader.readAsDataURL(file)
  }

  const statusMeta = statusOptionFor(selfPresence?.manualStatus)

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border shrink-0">
        <h2 className="text-base font-semibold text-foreground">{t("presence.settings")}</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile card */}
        <div className="mx-4 mt-4 p-4 rounded-xl bg-muted/50 border border-border">
          <div className="flex items-center gap-4">
            <div className="w-[84px] h-[84px] relative shrink-0">
              {/* The account's own avatar. The fallback used to be a stock
                  photo of a stranger, so every user without a picture saw the
                  same face — initials are honest about there being none. */}
              <AvatarPhoto
                src={editImage || profileImage}
                alt={displayName}
                className="w-full h-full rounded-full object-cover"
                fallback={
                  <div className="w-full h-full rounded-full bg-primary/15 flex items-center justify-center text-primary text-2xl font-semibold">
                    {initialsOf(displayName)}
                  </div>
                }
              />
              <span className={`absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${statusMeta.dot}`} />
              {isEditingProfile && (
                <>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"
                  >
                    <Camera className="w-5 h-5 text-white" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {isEditingProfile ? (
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full bg-background rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none border border-border"
                  autoFocus
                  placeholder="Your name"
                />
              ) : (
                <p className="font-semibold text-foreground">{displayName}</p>
              )}
              <div ref={statusPickerRef}>
                <button
                  onClick={() => setStatusPickerOpen(v => !v)}
                  className={`flex items-center gap-1.5 text-xs mt-0.5 ${statusMeta.color}`}
                >
                  <CircleDot className="w-3 h-3" />
                  {t(statusMeta.labelKey)}
                </button>
                {statusPickerOpen && (
                  <div className="absolute mt-1 bg-popover border border-border rounded-xl shadow-lg z-10 py-1 min-w-[160px]">
                    {STATUS_OPTIONS.map(s => (
                      <button
                        key={s.labelKey}
                        onClick={() => void handleSelectStatus(s.status)}
                        className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-muted text-left ${s.color}`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                        {t(s.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {isEditingProfile ? (
                <>
                  <button
                    onClick={() => {
                      onUpdateProfile?.(editName, editImage || profileImage || "")
                      setIsEditingProfile(false)
                      toast.success({ title: "Profile updated", description: "Your name and photo have been saved." })
                    }}
                    className="p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    title="Save"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setIsEditingProfile(false); setEditName(displayName); setEditImage("") }}
                    className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  {/* HIDDEN — profile editing lives in the account/profile
                      screen, not here. Kept commented rather than deleted so it
                      can come back without rebuilding the flow.
                  <button
                    onClick={() => { setIsEditingProfile(true); setEditName(displayName) }}
                    className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit profile"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  */}
                  <button
                    onClick={() => setShowQr(true)}
                    className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title={t("presence.myQrCode")}
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Status message
            *
            * With none set this read "Add a status message" — plainly an
            * action. With one set it rendered the message alone, in muted grey,
            * identical to every static label around it: nothing said it could
            * be changed, and removing it was possible only by opening it,
            * emptying the field and saving, which nobody would guess.
            *
            * So a set message now carries a pencil and sits in foreground text,
            * and clearing has its own control instead of being a side effect of
            * saving nothing. */}
          <div className="mt-4 pt-4 border-t border-border/50">
            {statusMessage ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={openStatusMessageDialog}
                  title={t("presence.editStatusMessage")}
                  className="flex-1 min-w-0 flex flex-col items-start text-left group"
                >
                  <span className="w-full flex items-center gap-2 text-sm text-foreground group-hover:text-primary transition-colors">
                    <span className="truncate">{statusMessage}</span>
                    <Edit2 className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                  </span>
                  {/* The only place a saved Clear After shows outside the
                      dialog — otherwise "1 Hour" would look like it had not
                      been kept until the message vanished. */}
                  {statusMessageExpiresAt && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {t("presence.statusClearsAt", { time: formatClearsAt(statusMessageExpiresAt, new Date()) })}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleClearStatusMessage}
                  title={t("presence.clearStatusMessage")}
                  aria-label={t("presence.clearStatusMessage")}
                  className="shrink-0 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={openStatusMessageDialog}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
              >
                {t("presence.addStatusMessage")}
              </button>
            )}
          </div>
        </div>

        {/* Notifications */}
        <SectionLabel>{t("presence.notifications")}</SectionLabel>
        <SettingRow
          icon={Bell}
          label={t("presence.muteGroupMessages")}
          right={<Toggle checked={notifPrefs.muteGroups} onChange={v => { void setMuteGroups(v); toast.info({ title: v ? "Group notifications muted" : "Group notifications unmuted" }) }} />}
        />
        <SettingRow
          icon={Bell}
          label={t("presence.mutePrivateMessages")}
          right={<Toggle checked={notifPrefs.muteDirect} onChange={v => { void setMuteDirect(v); toast.info({ title: v ? "Private notifications muted" : "Private notifications unmuted" }) }} />}
        />

        {/* Media */}
        <SectionLabel>{t("presence.autoDownload")}</SectionLabel>
        <SettingRow
          icon={Download}
          label={t("presence.photos")}
          right={<Toggle checked={autoPhotos} onChange={v => { setAutoDownload("photos", v); toast.info({ title: v ? "Photos will auto-download" : "Photo auto-download off" }) }} />}
        />
        <SettingRow
          icon={Download}
          label={t("presence.documents")}
          right={<Toggle checked={autoDocs} onChange={v => { setAutoDownload("documents", v); toast.info({ title: v ? "Documents will auto-download" : "Document auto-download off" }) }} />}
        />
        <SettingRow
          icon={Download}
          label="Videos"
          right={<Toggle checked={autoVideos} onChange={v => { setAutoDownload("videos", v); toast.info({ title: v ? "Videos will auto-download" : "Video auto-download off" }) }} />}
        />
        <SettingRow
          icon={Download}
          label="Audio"
          right={<Toggle checked={autoAudio} onChange={v => { setAutoDownload("audio", v); toast.info({ title: v ? "Audio will auto-download" : "Audio auto-download off" }) }} />}
        />

        {/* Privacy */}
        <SectionLabel>{t("presence.privacy")}</SectionLabel>
        <SettingRow
          icon={Shield}
          label={t("presence.readReceipts")}
          sublabel={t("presence.readReceiptsDesc")}
          right={<Toggle checked={privacy.readReceipts} onChange={v => { void setReadReceipts(v); toast.info({ title: v ? "Read receipts enabled" : "Read receipts disabled" }) }} />}
        />
        <SettingRow
          icon={User}
          label={t("presence.showStatus")}
          sublabel={t("presence.showStatusDesc")}
          right={<Toggle checked={privacy.showStatus} onChange={v => { void setShowStatus(v); toast.info({ title: v ? "Status is now visible" : "Status is now hidden" }) }} />}
        />
        <SettingRow icon={Lock} label={t("presence.blockedContacts")} onClick={onOpenBlockedContacts} />
        <SettingRow icon={Star} label={t("presence.starredMessages")} onClick={onOpenStarred} />

        {/* Appearance */}
        <SectionLabel>{t("presence.appearance")}</SectionLabel>
        <SettingRow icon={Palette} label={t("presence.chatBackground")} onClick={onOpenChatBackground} />
        <SettingRow icon={Moon} label={t("presence.theme")} sublabel={t("presence.systemDefault")} onClick={onOpenChatColor} />

        {/* HIDDEN — chat backup is not implemented yet (the button only ran
            a fake progress bar). Commented out rather than deleted so the UI
            can return when there is a real backup to run.
        {_/* Storage & Data *_/}
        <SectionLabel>{t("presence.storageData")}</SectionLabel>
        <div className="mx-4">
          <button
            onClick={() => setBackupExpanded(!backupExpanded)}
            className="flex items-center gap-3 w-full py-3 hover:bg-muted/40 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Database className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground text-start">{t("presence.chatBackup")}</p>
              <p className="text-xs text-muted-foreground mt-0.5 text-start">
                {backupAutoBackup ? `${t("presence.autoBackup")}: ${backupFrequency}` : t("presence.manualBackup")}
              </p>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground/50 transition-transform ${backupExpanded ? "rotate-180" : ""}`} />
          </button>

          {backupExpanded && (
            <div className="ml-11 mt-2 space-y-3 pb-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-foreground">{t("presence.includeMedia")}</span>
                <Toggle checked={backupIncludeMedia} onChange={setBackupIncludeMedia} />
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-foreground">{t("presence.autoBackup")}</span>
                <Toggle checked={backupAutoBackup} onChange={setBackupAutoBackup} />
              </div>
              {backupAutoBackup && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-foreground">{t("presence.frequency")}</span>
                  <select
                    value={backupFrequency}
                    onChange={e => setBackupFrequency(e.target.value)}
                    className="text-sm bg-muted border border-border rounded-lg px-3 py-1.5 outline-none"
                  >
                    <option value="Daily">{t("presence.daily")}</option>
                    <option value="Weekly">{t("presence.weekly")}</option>
                    <option value="Monthly">{t("presence.monthly")}</option>
                  </select>
                </div>
              )}
              <button
                onClick={() => {
                  if (isBackingUp) return
                  setIsBackingUp(true)
                  setBackupProgress(0)
                  const interval = setInterval(() => {
                    setBackupProgress(p => {
                      if (p >= 100) {
                        clearInterval(interval)
                        setIsBackingUp(false)
                        setBackupProgress(0)
                        toast.success({ title: "Backup complete", description: "Your chats have been backed up successfully." })
                        return 100
                      }
                      return p + 10
                    })
                  }, 200)
                }}
                disabled={isBackingUp}
                className="w-full py-2 mt-2 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-70"
              >
                {isBackingUp ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    {backupProgress}%
                  </span>
                ) : t("presence.backUpNow")}
              </button>
            </div>
          )}
        </div>

        */}

        <div className="h-6" />
      </div>

      {/* Set Status Message Dialog */}
      <Dialog open={statusMsgDialogOpen} onOpenChange={setStatusMsgDialogOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border">
            <DialogTitle className="text-lg font-semibold text-foreground">Set Status Message</DialogTitle>
          </DialogHeader>

          <div className="px-6 py-6 space-y-6">
            {/* Message input with char counter as end icon */}
            <div className="relative">
              <Input
                variant="outlined"
                placeholder="Enter message"
                value={draftMessage}
                autoFocus
                onChange={e => { if (e.target.value.length <= 50) setDraftMessage(e.target.value) }}
                // className="pr-14"
              />
              <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                {draftMessage.length}/50
              </span>
            </div>

            {/* Clear After — when the message removes itself. Saved with the
                message and enforced by the server, so it holds with this tab
                closed. */}
            <div className="space-y-2">
              <label htmlFor="status-clear-after" className="text-sm font-medium text-primary">
                {t("presence.clearAfter")}
              </label>
              <Select
                value={draftClearAfter.choice}
                onValueChange={v => handleClearAfterChoice(v as ClearAfter)}
              >
                <SelectTrigger id="status-clear-after" variant="outlined" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLEAR_AFTER_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {draftClearAfter.choice === "custom" && (
                <>
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <DatePicker
                      date={draftClearAfter.date}
                      minDate={new Date()}
                      setDate={date => {
                        const floor = earliestTimeFor(date, new Date(Date.now() + 60_000), CLEAR_AFTER_TIME_STEP)
                        setDraftClearAfter(prev => ({
                          ...prev,
                          date,
                          // Coming back to today can leave the time already
                          // behind us — moved forward rather than cleared.
                          time: isBefore(prev.time, floor) ? floor ?? prev.time : prev.time,
                        }))
                      }}
                      format="MM/dd/yyyy"
                      className="h-10 text-sm"
                      aria-invalid={customExpiryInvalid}
                    />
                    <TimePicker
                      value={draftClearAfter.time}
                      step={CLEAR_AFTER_TIME_STEP}
                      min={customTimeFloor}
                      onChange={time => setDraftClearAfter(prev => ({ ...prev, time }))}
                      aria-label={t("presence.clearAfterCustom")}
                      error={customExpiryInvalid}
                    />
                  </div>
                  {customExpiryInvalid && (
                    <p className="text-xs text-destructive">{t("presence.clearAfterPast")}</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => { setStatusMsgDialogOpen(false); setDraftMessage("") }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={async () => {
                const next = draftMessage.trim()
                // Resolved at the moment of saving, so "1 Hour" means an hour
                // from the click, not from when the dialog opened — including a
                // dialog that reopened on "1 Hour" and is saved with new text.
                const expiry = next ? resolveClearAfter(draftClearAfter, new Date()) : null
                // Save is disabled while the custom time is invalid; this only
                // catches the clock passing it between render and click.
                if (expiry === undefined) return
                const expiresAt = expiry?.toISOString() ?? null
                // Empty clears it. Save used to be disabled on an empty field,
                // which left no way to remove a status once set — only to
                // replace it with different text.
                setStatusMsgDialogOpen(false)
                // Confirm after it lands, as clearing already does. The success
                // toast used to fire alongside the request, so a save that
                // never reached the server still said "updated".
                try {
                  await setCustomMessage(next || null, expiresAt)
                  // Only once it has saved, so the note never names an option
                  // for a time the server does not hold.
                  rememberClearAfter(expiresAt, draftClearAfter.choice)
                  toast.success({
                    title: next ? "Status message updated" : "Status message cleared",
                  })
                } catch {
                  toast.error({ title: "Couldn't update status message" })
                }
              }}
              disabled={!dialogDirty || (draftTrimmed !== "" && customExpiryInvalid)}
            >
              {t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="max-w-xs text-center p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <QrCode className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{t("presence.myQrCode")}</h3>
              <p className="text-xs text-muted-foreground mt-1">{t("presence.scanToChat")}</p>
            </div>
            {/* QR placeholder grid */}
            <div className="w-44 h-44 bg-card border-2 border-border rounded-xl flex items-center justify-center">
              <div className="grid grid-cols-7 gap-0.5 p-2 w-full h-full">
                {Array.from({ length: 49 }).map((_, i) => (
                  <div key={i} className={`rounded-[1px] ${
                    [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,47,48,10,15,16,17,18,23,30,31,32,33,38].includes(i)
                      ? 'bg-foreground' : 'bg-transparent'
                  }`} />
                ))}
              </div>
            </div>
            <p className="text-xs font-mono text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">@{userHandle(authUser)}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
